import { Component, HostListener, Injectable, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, CanDeactivate, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  RolWorkflow, VersionWorkflow, Workflow, WorkflowsService, mensajeDeError,
} from 'src/app/services/workflows/workflows.service';
import {
  PerfilEditable, aEditable, errores, fecha, iguales, limpiarDevoluciones, mover, quitar,
} from '../workflows.dominio';

/**
 * Un workflow: sus perfiles en orden y, por cada uno, en quién cae al devolver y si puede rechazar
 * (al aprobar pasa siempre al siguiente). Maqueta aprobada por Juan el 2026-09-28; certificada el
 * mismo día (informe 04-certificacion-workflows.md, 15 hallazgos arreglados o anotados).
 *
 * 🔴 Lo publicado NO se edita: «Editar» abre la versión siguiente como borrador (o sigue el que ya
 * está), y las solicitudes que ya empezaron siguen con la suya.
 */
@Component({
  selector: 'app-workflow-detalle',
  templateUrl: './workflow-detalle.component.html',
  styleUrls: ['./workflow-detalle.component.css'],
})
export class WorkflowDetalleComponent implements OnInit, OnDestroy {
  cargando = true;
  guardando = false;
  error = '';
  noExiste = false;
  detalle: string[] = [];
  aviso = '';
  w: Workflow | null = null;
  roles: RolWorkflow[] = [];
  versiones: VersionWorkflow[] = [];
  puedeConfigurar = false;
  perfiles: PerfilEditable[] = [];
  private guardados: PerfilEditable[] = [];
  rolAgregar = '';
  confirmandoDescarte = false;
  fecha = fecha;
  private id = '';
  private sub?: Subscription;

  constructor(private _wf: WorkflowsService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    this.sub = this.route.paramMap.subscribe((p) => { this.id = p.get('id') || ''; this.cargar(); });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  /** Cerrar la pestaña con cambios sin guardar: el navegador pregunta. */
  @HostListener('window:beforeunload', ['$event'])
  alCerrar(e: BeforeUnloadEvent): void { if (this.sinGuardar) { e.preventDefault(); e.returnValue = ''; } }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = ''; this.detalle = []; this.aviso = ''; this.noExiste = false; this.confirmandoDescarte = false;
    try {
      const [w, roles, yo, versiones] = await Promise.all([
        this._wf.obtener(this.id), this._wf.roles(), this._wf.yo(), this._wf.versiones(this.id),
      ]);
      this.roles = roles || [];
      this.puedeConfigurar = !!yo?.puede_configurar;
      this.versiones = versiones || [];
      this.poner(w);
      // El aviso que dejó la pantalla anterior (p. ej., «se descartó el borrador»), una sola vez.
      const venia = (history.state && history.state.aviso) || '';
      if (venia) { this.aviso = venia; history.replaceState({ ...history.state, aviso: null }, ''); }
    } catch (e: any) {
      this.w = null;
      this.noExiste = e?.status === 404;
      this.error = mensajeDeError(e, 'No se pudo traer el workflow.');
    } finally {
      this.cargando = false;
    }
  };

  private poner(w: Workflow): void {
    this.w = w;
    this.perfiles = aEditable(w.perfiles);
    this.guardados = aEditable(w.perfiles);
  }

  // ── Estado de la pantalla ─────────────────────────────────────────────────

  get esBorrador(): boolean { return this.w?.estado === 'BORRADOR'; }
  get editable(): boolean { return this.esBorrador && this.puedeConfigurar; }
  get sinGuardar(): boolean { return this.editable && !iguales(this.perfiles, this.guardados); }
  get problemas(): string[] { return errores(this.perfiles); }
  get borradorAbierto(): VersionWorkflow | undefined { return this.versiones.find((v) => v.estado === 'BORRADOR'); }
  get publicadoVigente(): VersionWorkflow | undefined { return this.versiones.find((v) => v.estado === 'PUBLICADO'); }
  get puedenRechazar(): number { return this.perfiles.filter((p) => p.puede_rechazar).length; }
  get conDevolucion(): number { return this.perfiles.filter((p) => p.devuelve_a !== null).length; }

  /** La tarea del perfil en el puesto `i` (del tipo de solicitud, no se configura acá): sale de
   *  `w.ruta`, que ya la trae por perfil (certificación de experiencia, W-01). */
  tareaDe(i: number): string | null {
    return this.w?.ruta.find((r) => r.orden === i + 1)?.tarea || null;
  }

  nombreRol(id: string): string {
    const r = this.roles.find((x) => x._id === id);
    return r ? r.nombre : (this.w?.perfiles.find((p) => p.rol === id)?.rol_nombre || 'Rol');
  }

  rolUsado(id: string, i: number): boolean { return this.perfiles.some((p, k) => k !== i && p.rol === id); }
  get rolesLibres(): RolWorkflow[] { return this.roles.filter((r) => !this.perfiles.some((p) => p.rol === r._id)); }

  alAprobar(i: number): string {
    return i < this.perfiles.length - 1 ? this.nombreRol(this.perfiles[i + 1].rol) : (this.w?.fin || 'Fin');
  }

  /** La ruta que se dibuja arriba, con lo que está en pantalla (guardado o no). */
  get ruta(): Array<{ nombre: string; extra: string }> {
    return this.perfiles.map((p) => {
      const partes: string[] = [];
      if (p.devuelve_a !== null) partes.push('↩ ' + this.nombreRol(this.perfiles[p.devuelve_a - 1]?.rol || ''));
      if (p.puede_rechazar) partes.push('rechaza');
      return { nombre: this.nombreRol(p.rol), extra: partes.length ? partes.join(' · ') : 'solo aprueba' };
    });
  }

  // ── Edición (solo en borrador) ────────────────────────────────────────────

  cambiarRol(i: number, rol: string): void { this.perfiles = this.perfiles.map((p, k) => (k === i ? { ...p, rol } : p)); }
  cambiarDevuelve(i: number, v: string): void {
    this.perfiles = limpiarDevoluciones(this.perfiles.map((p, k) => (k === i ? { ...p, devuelve_a: v === '' ? null : Number(v) } : p)));
  }
  cambiarRechaza(i: number, v: boolean): void { this.perfiles = this.perfiles.map((p, k) => (k === i ? { ...p, puede_rechazar: v } : p)); }
  subir(i: number): void { this.perfiles = mover(this.perfiles, i, -1); }
  bajar(i: number): void { this.perfiles = mover(this.perfiles, i, 1); }
  sacar(i: number): void { this.perfiles = quitar(this.perfiles, i); }

  agregar(): void {
    if (!this.rolAgregar) return;
    const n = this.perfiles.length;
    this.perfiles = [...this.perfiles, { rol: this.rolAgregar, devuelve_a: n ? n : null, puede_rechazar: false }];
    this.rolAgregar = '';
  }

  deshacer(): void { this.perfiles = [...this.guardados]; }

  // ── Acciones contra el núcleo ─────────────────────────────────────────────

  private fallo(e: any, porOmision: string): void {
    this.error = mensajeDeError(e, porOmision);
    this.detalle = Array.isArray(e?.error?.detalle) ? e.error.detalle : [];
  }

  guardar = async (): Promise<boolean> => {
    if (!this.w || !this.editable) return false;
    this.guardando = true; this.error = ''; this.detalle = []; this.aviso = '';
    try {
      this.poner(await this._wf.guardar(this.w._id, this.perfiles));
      this.aviso = 'Cambios guardados en el borrador.';
      return true;
    } catch (e: any) {
      this.fallo(e, 'No se pudieron guardar los cambios.');
      return false;
    } finally {
      this.guardando = false;
    }
  };

  publicar = async (): Promise<void> => {
    if (!this.w || !this.editable) return;
    if (errores(this.perfiles, true).length) { this.error = errores(this.perfiles, true)[0]; return; }
    if (this.sinGuardar && !(await this.guardar())) return;
    this.guardando = true; this.error = ''; this.detalle = []; this.aviso = '';
    try {
      const w = await this._wf.publicar(this.w._id);
      this.poner(w);
      this.versiones = await this._wf.versiones(w._id);
      this.aviso = `Versión ${w.version} publicada. Rige para las solicitudes que entren desde ahora.`;
    } catch (e: any) {
      this.fallo(e, 'No se pudo publicar.');
    } finally {
      this.guardando = false;
    }
  };

  /** «Editar» sobre lo publicado: abre (o sigue) el borrador de la versión siguiente. */
  editar = async (): Promise<void> => {
    if (!this.w || !this.puedeConfigurar) return;
    this.guardando = true; this.error = ''; this.detalle = [];
    try {
      const b = await this._wf.abrirBorrador(this.w.tipo_solicitud);
      this.router.navigate(['/console/workflows', b._id]);
    } catch (e: any) {
      this.fallo(e, 'No se pudo abrir el borrador.');
    } finally {
      this.guardando = false;
    }
  };

  /** Descartar va en dos pasos, en la misma línea (nada de ventanas emergentes). */
  descartar = async (): Promise<void> => {
    if (!this.w || !this.editable) return;
    this.guardando = true; this.error = ''; this.detalle = [];
    const version = this.w.version;
    try {
      await this._wf.descartar(this.w._id);
      this.guardados = [...this.perfiles];   // lo descartado no cuenta como «sin guardar» al salir
      const pub = this.publicadoVigente;
      const aviso = `Se descartó el borrador de la v${version}.` + (pub ? ` Sigue rigiendo la v${pub.version}.` : '');
      this.router.navigate(pub ? ['/console/workflows', pub._id] : ['/console/workflows'], { state: { aviso } });
    } catch (e: any) {
      this.fallo(e, 'No se pudo descartar el borrador.');
    } finally {
      this.guardando = false;
      this.confirmandoDescarte = false;
    }
  };
}

/** Salir de un borrador con cambios sin guardar pide confirmación. */
@Injectable({ providedIn: 'root' })
export class WorkflowSinGuardarGuard implements CanDeactivate<WorkflowDetalleComponent> {
  canDeactivate(c: WorkflowDetalleComponent): boolean {
    return !c.sinGuardar || window.confirm('Tienes cambios sin guardar en este borrador. ¿Salir y perderlos?');
  }
}
